import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, Phone, Mail, Clock, Instagram, Facebook, Twitter, Linkedin } from "lucide-react";
import { LOGO_URL, CONTACT, SOCIALS } from "@/data/content";
import { useConsult } from "@/components/ConsultProvider";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/admissions", label: "Admissions" },
  { to: "/visa-guidance", label: "Visa Guidance" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact Us" },
];

const SOCIAL_ICONS = { Instagram, Facebook, "Twitter / X": Twitter, LinkedIn: Linkedin };

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openConsult } = useConsult();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50"
    >
      <div
        className={`hidden overflow-hidden bg-navy text-white transition-all duration-500 lg:block ${
          scrolled ? "max-h-0" : "max-h-12"
        }`}
        data-testid="nav-utility-bar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2.5">
          <div className="flex items-center gap-7 text-xs text-slate-300">
            <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} data-testid="nav-top-phone" className="flex items-center gap-2 transition-colors hover:text-gold">
              <Phone className="h-3.5 w-3.5 text-gold" /> {CONTACT.phonePrimary}
            </a>
            <a href={`mailto:${CONTACT.emailPrimary}`} data-testid="nav-top-email" className="flex items-center gap-2 transition-colors hover:text-gold">
              <Mail className="h-3.5 w-3.5 text-gold" /> {CONTACT.emailPrimary}
            </a>
            <span className="hidden items-center gap-2 xl:flex">
              <Clock className="h-3.5 w-3.5 text-gold" /> {CONTACT.hours}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.name];
              return (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  data-testid={`nav-top-social-${s.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-slate-300 transition-colors duration-300 hover:bg-gold hover:text-white"
                >
                  <Icon className="h-3.5 w-3.5" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className={`border-b border-line bg-white/90 backdrop-blur-xl transition-shadow duration-300 ${
          scrolled ? "shadow-soft" : ""
        }`}
      >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" aria-label="Student First Overseas home">
          <span className="inline-flex items-center rounded-full bg-white px-5 py-2 shadow-soft ring-1 ring-line">
            <img src={LOGO_URL} alt="Student First Overseas logo" className="h-9 w-auto" loading="eager" />
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary navigation">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `gold-underline whitespace-nowrap py-1 font-accent text-[13px] font-semibold uppercase tracking-widest transition-colors ${
                  isActive ? "active text-gold" : "text-navy hover:text-gold"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 font-accent text-xs font-semibold uppercase tracking-widest text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <button
            onClick={() => openConsult()}
            data-testid="nav-consult-button"
            className="rounded-full bg-gold px-6 py-2.5 font-accent text-xs font-bold uppercase tracking-widest text-white shadow-soft transition-colors duration-300 hover:bg-gold-soft"
          >
            Free Consultation
          </button>
        </div>

        <button
          className="rounded-md p-2 text-navy lg:hidden"
          onClick={() => setMobileOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            key={location.pathname}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-surface lg:hidden"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 font-accent text-sm font-semibold uppercase tracking-widest ${
                      isActive ? "bg-navy text-white" : "text-navy hover:bg-line/60"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => { setMobileOpen(false); openConsult(); }}
                data-testid="nav-mobile-consult-button"
                className="mt-2 rounded-full bg-navy px-6 py-3.5 font-accent text-xs font-semibold uppercase tracking-widest text-white"
              >
                Free Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
